import * as Sentry from '@sentry/vue';
import { ref } from 'vue';

// Native update handling — two independent concerns, both native-only (gated on window.kgrCapacitor,
// plugins imported dynamically so none of this reaches the web bundle):
//
//  1. OTA web-bundle update (Capgo, self-hosted static, MANUAL mode): swaps the JS/HTML/CSS bundle
//     without a store release. Manifest at <otaBaseUrl>/ota/<package>/manifest.json -> { version, url }
//     (no env/channel segment — the env is the host: prod = app.kigaroo.de, stage = stageN.kigaroo.com).
//     Bundle version uses the OTA patch lane <major>.<minor>.<run_number>. OTA updates are RESTRICTED
//     to the same major.minor — a 16.0.x app only accepts 16.0.y bundles, never 16.1.x or 99.0.x. This
//     prevents test builds from affecting production and guards against native API mismatches. Users on
//     older major.minor versions are migrated via the forced store update (see #2 below), then receive
//     OTA updates in the new lane. See NATIVE_BUILDS.md ("OTA").
//
//  2. Forced native update: when a newer NATIVE binary is in the store, force the user to it. Driven by
//     ACTUAL store availability (@capawesome/capacitor-app-update), NOT a manifest version floor — so a
//     build can never lock users out before its new version is actually live in the store. Android uses
//     Play In-App Updates (immediate, in-app); iOS shows a blocking overlay + a button to the App Store.

interface OtaManifest {
    version: string
    url: string
}

// Module-level singletons — shared by every useCapgo() caller and the overlay.
const otaUpdating = ref(false);
const otaProgress = ref(0);
const forceStoreUpdate = ref(false);

let checking = false;
let checkingForced = false;

// "<version>:<attempts>" — how often we handed this exact version to set() without ending up on it.
// Survives the reload (the webview origin does not change when Capgo swaps the bundle path), which
// is what lets a retry be told apart from an endless loop.
const OTA_APPLIED_KEY = 'kgr_ota_apply_attempts';
// A first apply can fail for reasons that are gone on the next try (the observed case: attempt two
// sticks), so do NOT block after one failure — only stop once the same version has clearly proven
// that it cannot be applied on this device.
const OTA_MAX_APPLY_ATTEMPTS = 3;

function readApplyAttempts(version: string): number {
    const [storedVersion, attempts] = (localStorage.getItem(OTA_APPLIED_KEY) ?? '').split(':');
    return storedVersion === version ? Number.parseInt(attempts, 10) || 0 : 0;
}
// Human-readable record of the last failed apply, for the Impressum developer panel — the only way
// to see what happened on a device without a log connection.
const OTA_FAILURE_KEY = 'kgr_ota_last_failure';

export function readOtaFailure(): string | null {
    try {
        return localStorage.getItem(OTA_FAILURE_KEY);
    } catch {
        return null;
    }
}

function recordOtaFailure(reason: string): void {
    const entry = `${new Date().toISOString().slice(0, 16).replace('T', ' ')} — ${reason}`;
    localStorage.setItem(OTA_FAILURE_KEY, entry);
    Sentry.captureMessage(`[OTA] ${reason}`, 'error');
}

export function useCapgo() {
    return { otaUpdating, otaProgress, forceStoreUpdate };
}

// Parse "15.0.412" / "v15.0.0" -> [15, 0, 412]; prerelease/build metadata is dropped.
function parseVersion(v: string): [number, number, number] {
    const core = v.replace(/^v/, '').split(/[-+]/)[0];
    const [maj, min, pat] = core.split('.').map((n) => Number.parseInt(n, 10) || 0);
    return [maj ?? 0, min ?? 0, pat ?? 0];
}

function compareVersion(a: string, b: string): number {
    const pa = parseVersion(a);
    const pb = parseVersion(b);
    for (let i = 0; i < 3; i++) {
        if (pa[i] !== pb[i]) {
            return pa[i] - pb[i];
        }
    }
    return 0;
}

// Native-only, and the FIRST thing each app's main.ts runs: tells Capgo this bundle booted, which
// cancels the appReadyTimeout rollback (10s, see capacitor.config.ts).
//
// Must not sit behind ANY network I/O. It used to run in App.vue's onMounted, but the mount itself
// waits for `router.isReady()`, whose guard awaits `loadLocale()` — an untimed catalogue fetch. On a
// slow connection (a fresh install right after downloading the OTA zip is exactly that) the
// confirmation missed the 10s deadline, Capgo rolled the healthy bundle back and reloaded, the next
// check saw the manifest as newer again → download, apply, roll back, repeat. Running it at module
// scope keeps a crash-on-boot safety net (a bundle whose entry chunk fails to load or throws never
// gets here) without tying it to the API being reachable.
export async function notifyCapgoAppReady(): Promise<void> {
    try {
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
        await CapacitorUpdater.notifyAppReady();
    } catch (e) {
        console.error('[useCapgo] notifyAppReady failed', e);
    }
}

// Native-only. Called once on every boot from App.vue (onMounted), behind window.kgrCapacitor.
// Forces a native store update if one is available, then checks for an OTA bundle on launch and on
// every resume to foreground. Confirming the bundle is notifyCapgoAppReady() above (from main.ts).
export async function initCapgo(): Promise<void> {
    await checkForcedUpdate();
    await checkForUpdate();

    const { App } = await import('@capacitor/app');
    App.addListener('appStateChange', async ({ isActive }) => {
        if (!isActive) {
            return;
        }
        // Forced native update first: a user who dismissed the immediate flow (or an iOS binary that
        // went live while the app was backgrounded) must be re-gated here — otherwise the forced
        // update would only re-appear on the next cold start. OTA runs after and is skipped while forced.
        await checkForcedUpdate();
        await checkForUpdate();
    });
}

// Forced "update from the store" — gated on real store availability, so it only fires when a newer
// binary actually exists in the store (no lockout on a premature version bump). Runs on boot and on
// every resume. Re-entrancy guard: Google's immediate flow backgrounds+foregrounds the app, which
// re-fires appStateChange — without the guard that resume would launch a second immediate flow.
async function checkForcedUpdate(): Promise<void> {
    if (checkingForced || forceStoreUpdate.value) {
        return;
    }
    checkingForced = true;
    try {
        const { AppUpdate, AppUpdateAvailability } = await import('@capawesome/capacitor-app-update');
        const info = await AppUpdate.getAppUpdateInfo();
        if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
            return;
        }

        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.getPlatform() === 'android' && info.immediateUpdateAllowed) {
            // Google's immediate flow is user-cancellable despite the name. A *successful* update
            // installs and restarts the app — so if control returns here at all (rejected on cancel,
            // or resolved without a restart), the update did NOT go through. Fall through to the
            // blocking overlay so the forced update can't be ignored until the next cold start.
            try {
                await AppUpdate.performImmediateUpdate();
            } catch (e) {
                console.warn('[useCapgo] immediate update dismissed/failed, falling back to overlay', e);
            }
        }
        // iOS, Android-immediate-not-allowed, or a dismissed Android immediate flow: hard-block with
        // the overlay + send to the store.
        forceStoreUpdate.value = true;
    } catch (e) {
        // Not installed from a store (sideloaded / Firebase QA) → getAppUpdateInfo throws; no force.
        console.error('[useCapgo] app-update check failed', e);
    } finally {
        checkingForced = false;
    }
}

async function checkForUpdate(): Promise<void> {
    if (checking || checkingForced || otaUpdating.value || forceStoreUpdate.value) {
        return;
    }
    const { otaBaseUrl, otaPath } = window.kgrDynamicConfig;
    if (!otaBaseUrl) {
        return;
    } // OTA not configured for this build

    checking = true;
    try {
        const { App } = await import('@capacitor/app');
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
        const info = await App.getInfo();

        const manifestUrl = `${otaBaseUrl}${otaPath ?? '/ota'}/${info.id}/manifest.json`;
        const res = await fetch(manifestUrl, { cache: 'no-store' });
        if (!res.ok) {
            return;
        }
        const manifest = (await res.json()) as OtaManifest;

        // The plugin records a bundle it rolled back (notifyAppReady missing, or one that failed to
        // boot) and clears that record on first read — so read it here and persist the reason where
        // it can be read back without a log connection (Impressum developer panel + Sentry).
        // Without it a rollback is silent and only surfaces as "the update banner is back".
        try {
            const failed = await CapacitorUpdater.getFailedUpdate();
            if (failed?.bundle?.version) {
                console.error('[useCapgo] previous bundle was rolled back by the plugin:', failed.bundle.version);
                recordOtaFailure(`${failed.bundle.version} zurückgerollt (notifyAppReady fehlte oder Boot-Fehler)`);
            }
        } catch (e) {
            console.warn('[useCapgo] getFailedUpdate not available', e);
        }

        const current = await CapacitorUpdater.current();
        console.log('[useCapgo] running bundle', current.bundle.version, '— manifest offers', manifest.version);
        if (compareVersion(manifest.version, current.bundle.version) <= 0) {
            // Arrived — the attempt counter has done its job.
            if (current.bundle.version === manifest.version) {
                localStorage.removeItem(OTA_APPLIED_KEY);
            }
            return;
        }

        // OTA is the PATCH lane only: major.minor must match. A store release bumps major.minor,
        // and the forced-update mechanism (checkForcedUpdate) ensures users migrate to it. This
        // prevents a test build (e.g. 99.0.x) from accidentally updating production apps, and
        // guards against OTA bundles that assume newer native code than the running binary has.
        const [mMaj, mMin] = parseVersion(manifest.version);
        const [cMaj, cMin] = parseVersion(current.bundle.version);
        if (mMaj !== cMaj || mMin !== cMin) {
            console.log('[useCapgo] skipping OTA — major.minor mismatch:', `${cMaj}.${cMin}`, '≠', `${mMaj}.${mMin}`);
            return;
        }

        // Applied repeatedly and still not running it: the plugin keeps rolling this bundle back or
        // refusing it. Stop instead of looping the user through download → apply → banner forever;
        // a NEWER version is unaffected and will be tried normally.
        const attempts = readApplyAttempts(manifest.version);
        if (attempts >= OTA_MAX_APPLY_ATTEMPTS) {
            console.warn('[useCapgo] bundle', manifest.version, `not applicable after ${attempts} attempts — giving up`);
            return;
        }

        otaUpdating.value = true;
        otaProgress.value = 0;
        const downloadListener = await CapacitorUpdater.addListener('download', (state) => {
            otaProgress.value = Math.round(state.percent);
        });
        try {
            // manifest.url is relative to the manifest (the workflow writes "<version>.zip").
            const zipUrl = new URL(manifest.url, manifestUrl).toString();
            const bundle = await CapacitorUpdater.download({ url: zipUrl, version: manifest.version });
            console.log('[useCapgo] downloaded', bundle.version, bundle.id, '— applying');
            // Written BEFORE set(): the call reloads the webview, so nothing after it runs.
            localStorage.setItem(OTA_APPLIED_KEY, `${manifest.version}:${attempts + 1}`);
            await CapacitorUpdater.set(bundle); // reloads the webview into the new bundle
        } finally {
            await downloadListener.remove();
        }
    } catch (e) {
        // Also the path for a REJECTED set() — the plugin refuses a bundle it has marked as failed,
        // and one whose version is not above the native versionName. The overlay closes and the app
        // keeps running the old bundle, so this looks like a completed update from the outside.
        console.error('[useCapgo] update check failed', e);
        recordOtaFailure(`Download/set fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
        otaUpdating.value = false; // don't strand the user on the overlay if download/set failed
    } finally {
        checking = false;
    }
}

// Opens the store listing for the forced-update gate (iOS button). Android uses the in-app flow
// above and never reaches this; iOS needs the numeric App Store id from the runtime config.
export async function openStore(): Promise<void> {
    const { AppUpdate } = await import('@capawesome/capacitor-app-update');
    const appId = window.kgrDynamicConfig.iosAppStoreId;
    await AppUpdate.openAppStore(appId ? { appId } : undefined);
}
